import { useMarketingStore } from '../../store/marketing.store';
import InfoTooltip from '../revenue/InfoTooltip';

function formatNum(value: number): string {
  return value.toLocaleString('en-US');
}

function formatVND(value: number): string {
  return Math.round(value).toLocaleString('en-US') + ' VND';
}

interface FunnelStep {
  label: string;
  value: number;
  source: string;
  color: string;
  textColor: string;
}

export default function UnifiedFunnelChart({ isDark }: { isDark: boolean }) {
  const unifiedFunnel = useMarketingStore((s) => s.unifiedFunnel);

  const tooltipText =
    'Hành trình khách hàng từ quảng cáo đến mua gói, tổng hợp từ 3 hệ thống:\n\n' +
    '• Facebook Ads: Quảng cáo hiển thị → Click → Nhắn tin\n' +
    '• HubSpot CRM: Lead (cho số điện thoại) → MQL (đặt lịch tập thử) → OPP (tập thử OK) → Customer (mua gói)\n' +
    '• Pancake: Đối chiếu số liệu từ tag trên hội thoại\n\n' +
    'Đã loại bỏ các kênh không phải quảng cáo (Referral, Direct Sales, Voucher, Transfer, KOLs).';

  if (!unifiedFunnel) {
    return (
      <div className={`backdrop-blur rounded-lg p-6 ${isDark ? 'bg-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Phễu Marketing Thống Nhất
          <InfoTooltip isDark={isDark} text={tooltipText} />
        </h3>
        <div className={`flex items-center justify-center h-64 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
          Chưa có dữ liệu
        </div>
      </div>
    );
  }

  const steps: (FunnelStep & { tooltip?: string })[] = [
    { label: 'Impressions', value: unifiedFunnel.impressions, source: 'Facebook', color: 'bg-blue-500', textColor: 'text-blue-400', tooltip: 'Số lần quảng cáo hiển thị trước mắt người dùng trên Facebook/Instagram.\n\n1 người có thể thấy quảng cáo nhiều lần = nhiều impressions.\n\n📡 Nguồn: Facebook Ads API (Meta Graph API v21.0)\n📂 API: /{ad_account_id}/insights\n🏷️ Field: impressions' },
    { label: 'Clicks', value: unifiedFunnel.clicks, source: 'Facebook', color: 'bg-cyan-500', textColor: 'text-cyan-400', tooltip: 'Số lần người dùng nhấn vào quảng cáo.\n\nBao gồm click vào link, nút CTA, ảnh, video trong quảng cáo.\n\n📡 Nguồn: Facebook Ads API (Meta Graph API v21.0)\n📂 API: /{ad_account_id}/insights\n🏷️ Field: clicks' },
    { label: 'Messages', value: unifiedFunnel.messages, source: 'Pancake', color: 'bg-amber-500', textColor: 'text-amber-400', tooltip: 'Số khách inbox lần đầu trong kỳ — mỗi conversation INBOX trên Pancake được đếm 1 lần ở thời điểm tạo (inserted_at). Tin nhắn tiếp theo trong cùng conversation không tính lại.\n\n📡 Nguồn: Pancake (pages.fm) — public_api/v2/conversations\n🏷️ Filter: type=INBOX, group by inserted_at' },
    { label: 'Lead', value: unifiedFunnel.leads, source: 'HubSpot', color: 'bg-emerald-500', textColor: 'text-emerald-400', tooltip: 'Khách hàng tiềm năng — người đã cho số điện thoại hoặc thông tin liên hệ sau khi được tư vấn qua chat.\n\nĐược Convert gán nhãn "Lead" trên Pancake và cập nhật lên HubSpot.\n\n📡 Nguồn: HubSpot CRM API\n📂 API: /crm/v3/objects/contacts/search\n🏷️ Field: hs_v2_date_entered_lead\n🔍 Lọc: Loại bỏ kênh không phải quảng cáo (Referral, Direct Sales, Voucher...)\n\n🔗 Report HubSpot: https://app.hubspot.com/reports-list/6595822/165706379/' },
    { label: 'MQL', value: unifiedFunnel.mql, source: 'HubSpot', color: 'bg-teal-500', textColor: 'text-teal-400', tooltip: 'Marketing Qualified Lead — khách hàng đủ điều kiện marketing.\n\nBao gồm 2 trường hợp:\n• Khách hàng cho số điện thoại và được đặt lịch tập thử\n• Khách hàng không cho số điện thoại nhưng đến trực tiếp studio (outdoor)\n\nConvert gán nhãn "MQL" trên Pancake.\n\n📡 Nguồn: HubSpot CRM API\n📂 API: /crm/v3/objects/contacts/search\n🏷️ Field: hs_v2_date_entered_marketingqualifiedlead\n\n🔗 Report HubSpot: https://app.hubspot.com/reports-list/6595822/165706478/' },
    { label: 'OPP', value: unifiedFunnel.opp, source: 'HubSpot', color: 'bg-violet-500', textColor: 'text-violet-400', tooltip: 'Opportunity — khách hàng đã đến studio và tập thử thành công.\n\nĐiều kiện:\n• Có ngày booking trial (date_booking)\n• Trial 1 status = Yes (trial_1_status)\n• Lifecycle stage = Opportunity hoặc Customer (lifecyclestage)\n\nConvert gán nhãn "OPP" trên Pancake.\n\n📡 Nguồn: HubSpot CRM API\n📂 API: /crm/v3/objects/contacts/search\n🏷️ Fields: date_booking + trial_1_status + lifecyclestage\n\n🔗 Report HubSpot: https://app.hubspot.com/reports-list/6595822/165706498/' },
    { label: 'Customer', value: unifiedFunnel.customers, source: 'HubSpot', color: 'bg-pink-500', textColor: 'text-pink-400', tooltip: 'Khách hàng chính thức — đã mua gói tập sau khi được sale tư vấn tại studio.\n\nĐây là bước cuối cùng: thấy quảng cáo → chat → tập thử → mua gói.\n\n📡 Nguồn: HubSpot CRM API\n📂 API: /crm/v3/objects/contacts/search\n🏷️ Field: hs_v2_date_entered_customer\n\n🔗 Report HubSpot: https://app.hubspot.com/reports-list/6595822/165945202/' },
  ];

  const maxValue = Math.max(...steps.map((s) => s.value), 1);

  const rates = [
    { label: 'Click Rate', value: unifiedFunnel.ctr, tooltip: 'Click Rate (CTR) — Tỷ lệ click\n\nCông thức: Clicks ÷ Impressions\n\nTrong 100 lần quảng cáo hiển thị, có bao nhiêu lần người ta click vào.' },
    { label: 'Lead Rate', value: unifiedFunnel.leadRate, tooltip: 'Lead Rate — Tỷ lệ thành Lead\n\nCông thức: Leads ÷ Messages\n\nTrong số người nhắn tin tư vấn, bao nhiêu % cho số điện thoại trở thành khách hàng tiềm năng.' },
    { label: 'MQL Rate', value: unifiedFunnel.mqlRate, tooltip: 'MQL Rate — Tỷ lệ đủ điều kiện marketing\n\nCông thức: MQL ÷ Leads\n\nTrong số Lead, bao nhiêu % được đặt lịch tập thử hoặc đến trực tiếp studio.' },
    { label: 'OPP Rate', value: unifiedFunnel.oppRate, tooltip: 'Opportunity Rate — Tỷ lệ tập thử thành công\n\nCông thức: OPP ÷ MQL\n\nTrong số khách hàng đủ điều kiện, bao nhiêu % đã đến studio tập thử thành công.' },
    { label: 'Customer Rate', value: unifiedFunnel.custRate, tooltip: 'Customer Rate — Tỷ lệ mua gói\n\nCông thức: Customers ÷ OPP\n\nTrong số khách hàng tập thử thành công, bao nhiêu % cuối cùng mua gói tập.' },
  ];

  const sourceColors: Record<string, string> = {
    Facebook: isDark ? 'text-blue-400' : 'text-blue-600',
    HubSpot: isDark ? 'text-orange-400' : 'text-orange-600',
    Pancake: isDark ? 'text-green-400' : 'text-green-600',
  };

  return (
    <div className={`backdrop-blur rounded-lg p-6 ${isDark ? 'bg-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <h3 className={`text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        Phễu Marketing Thống Nhất
        <InfoTooltip isDark={isDark} text={tooltipText} />
      </h3>
      <p className={`text-xs mb-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
        Facebook Ads + HubSpot CRM + Pancake Tags
      </p>

      {/* Journey illustration */}
      <div className={`rounded-xl p-4 mb-5 ${isDark ? 'bg-white/[0.03] border border-white/[0.06]' : 'bg-gray-50 border border-gray-200'}`}>
        <p className={`text-[11px] uppercase tracking-wider font-medium mb-3 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Hành trình khách hàng</p>
        <div className="flex items-center gap-0 overflow-x-auto pb-1">
          {[
            { icon: '📱', label: 'Thấy quảng cáo', sub: 'Facebook / Instagram', color: 'from-blue-500/20 to-blue-500/5', border: 'border-blue-500/30', text: 'text-blue-400' },
            { icon: '👆', label: 'Click quảng cáo', sub: '', color: 'from-cyan-500/20 to-cyan-500/5', border: 'border-cyan-500/30', text: 'text-cyan-400' },
            { icon: '💬', label: 'Nhắn tin tư vấn', sub: 'Pancake', color: 'from-amber-500/20 to-amber-500/5', border: 'border-amber-500/30', text: 'text-amber-400' },
            { icon: '📋', label: 'Cho số điện thoại', sub: 'Lead', color: 'from-emerald-500/20 to-emerald-500/5', border: 'border-emerald-500/30', text: 'text-emerald-400' },
            { icon: '📅', label: 'Đặt lịch tập thử', sub: 'MQL', color: 'from-teal-500/20 to-teal-500/5', border: 'border-teal-500/30', text: 'text-teal-400' },
            { icon: '🏋️', label: 'Tập thử thành công', sub: 'OPP', color: 'from-violet-500/20 to-violet-500/5', border: 'border-violet-500/30', text: 'text-violet-400' },
            { icon: '🎉', label: 'Mua gói tập', sub: 'Customer', color: 'from-pink-500/20 to-pink-500/5', border: 'border-pink-500/30', text: 'text-pink-400' },
          ].map((step, idx, arr) => (
            <div key={step.label} className="flex items-center shrink-0">
              <div className={`flex flex-col items-center text-center w-[72px] sm:w-[100px] rounded-lg border bg-gradient-to-b p-2 sm:p-2.5 ${step.color} ${step.border}`}>
                <span className="text-lg sm:text-xl leading-none mb-1">{step.icon}</span>
                <span className={`text-[9px] sm:text-[10px] font-medium leading-tight ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>{step.label}</span>
                {step.sub && <span className={`text-[8px] sm:text-[9px] mt-0.5 ${step.text}`}>{step.sub}</span>}
              </div>
              {idx < arr.length - 1 && (
                <span className={`text-sm mx-0.5 shrink-0 ${isDark ? 'text-slate-600' : 'text-gray-300'}`}>→</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Funnel bars */}
      <div className="space-y-1 mb-6">
        {steps.map((step, idx) => {
          const widthPct = Math.max((step.value / maxValue) * 100, 2);
          const prevStep = steps[idx - 1];
          let convRate: number | null = null;
          if (prevStep && prevStep.value > 0) {
            convRate = parseFloat(((step.value / prevStep.value) * 100).toFixed(2));
          }

          return (
            <div key={step.label}>
              {convRate !== null && (
                <div className={`flex items-center gap-1 py-0.5 pl-4 text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  <span>↓</span>
                  <span className="font-mono">{convRate}%</span>
                  <span className="italic">chuyển đổi</span>
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="w-28 text-right shrink-0 flex flex-col items-end">
                  <span className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    {step.label}
                    {step.tooltip && <InfoTooltip isDark={isDark} text={step.tooltip} />}
                  </span>
                  <span className={`text-[10px] ${sourceColors[step.source]}`}>
                    ({step.source})
                  </span>
                </div>
                <div className="flex-1 relative flex items-center gap-2">
                  <div
                    className={`h-8 rounded-md ${step.color} flex items-center ${widthPct > 15 ? 'justify-end pr-3' : ''} transition-all`}
                    style={{ width: `${widthPct}%`, opacity: isDark ? 0.7 : 0.8 }}
                  >
                    {widthPct > 15 && (
                      <span className="text-xs font-bold font-mono text-white drop-shadow">
                        {formatNum(step.value)}
                      </span>
                    )}
                  </div>
                  {widthPct <= 15 && (
                    <span className={`text-xs font-bold font-mono ${isDark ? step.textColor : 'text-gray-700'}`}>
                      {formatNum(step.value)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary scorecards */}
      {(() => {
        const totalSpend = unifiedFunnel.spend;
        const totalLeads = unifiedFunnel.leads;
        const avgCPL = totalLeads > 0 ? totalSpend / totalLeads : 0;
        const avgCPC = unifiedFunnel.clicks > 0 ? totalSpend / unifiedFunnel.clicks : 0;

        const cards = [
          { label: 'Tổng chi phí quảng cáo', value: formatVND(totalSpend), color: 'text-cyan-400', tooltip: 'Tổng ngân sách đã chi cho quảng cáo trong kỳ.\n\n📡 Nguồn: Facebook Ads API (Meta Graph API v21.0)\n📂 API: /{ad_account_id}/insights\n🏷️ Field: spend\n\nGộp từ 2 ad accounts: 1A + 2A' },
          { label: 'Tổng Clicks', value: formatNum(unifiedFunnel.clicks), color: 'text-cyan-400', tooltip: 'Số lần người dùng nhấn vào quảng cáo.\n\n📡 Nguồn: Facebook Ads API\n🏷️ Field: clicks' },
          { label: 'Tổng Messages', value: formatNum(unifiedFunnel.messages), color: 'text-amber-400', tooltip: 'Số khách inbox lần đầu trong kỳ.\n\n📡 Nguồn: Pancake (pages.fm) — public_api/v2/conversations\n🏷️ Filter: type=INBOX, group by inserted_at\n\nMỗi khách chỉ tính 1 lần ở thời điểm tạo conversation.' },
          { label: 'Tổng Lead', value: formatNum(totalLeads), color: 'text-emerald-400', tooltip: 'Số khách hàng tiềm năng — đã cho số điện thoại hoặc thông tin liên hệ.\n\n📡 Nguồn: HubSpot CRM API\n🏷️ Field: hs_v2_date_entered_lead\n🔍 Lọc: Chỉ tính nguồn quảng cáo, loại bỏ Referral, Direct Sales, Voucher...' },
          { label: 'Tổng MQL', value: formatNum(unifiedFunnel.mql), color: 'text-teal-400', tooltip: 'Marketing Qualified Lead — khách hàng đủ điều kiện marketing.\n\n📡 Nguồn: HubSpot CRM API\n🏷️ Field: hs_v2_date_entered_marketingqualifiedlead' },
          { label: 'Tổng OPP', value: formatNum(unifiedFunnel.opp), color: 'text-violet-400', tooltip: 'Opportunity — khách hàng đã tập thử thành công.\n\n📡 Nguồn: HubSpot CRM API\n🏷️ Fields: date_booking + trial_1_status + lifecyclestage' },
          { label: 'Tổng Customer', value: formatNum(unifiedFunnel.customers), color: 'text-pink-400', tooltip: 'Khách hàng chính thức — đã mua gói tập.\n\n📡 Nguồn: HubSpot CRM API\n🏷️ Field: hs_v2_date_entered_customer' },
          { label: 'Cost per Click', value: formatVND(avgCPC), color: 'text-cyan-400', tooltip: 'Chi phí trung bình cho 1 lượt nhấn quảng cáo.\n\nCông thức: Tổng chi phí ÷ Tổng clicks' },
          { label: 'Cost per Message', value: formatVND(unifiedFunnel.messages > 0 ? totalSpend / unifiedFunnel.messages : 0), color: 'text-amber-400', tooltip: 'Chi phí trung bình để có 1 khách inbox lần đầu.\n\nCông thức: Tổng chi phí FB ÷ Tổng tin nhắn mới Pancake' },
          { label: 'Cost per Lead', value: formatVND(avgCPL), color: 'text-emerald-400', tooltip: 'Chi phí trung bình để có 1 khách hàng tiềm năng.\n\nCông thức: Tổng chi phí ÷ Tổng leads' },
          { label: 'Cost per MQL', value: formatVND(unifiedFunnel.mql > 0 ? totalSpend / unifiedFunnel.mql : 0), color: 'text-teal-400', tooltip: 'Chi phí trung bình để có 1 khách hàng đủ điều kiện marketing.\n\nCông thức: Tổng chi phí ÷ Tổng MQL' },
          { label: 'Cost per OPP', value: formatVND(unifiedFunnel.opp > 0 ? totalSpend / unifiedFunnel.opp : 0), color: 'text-violet-400', tooltip: 'Chi phí trung bình để có 1 khách hàng tập thử thành công.\n\nCông thức: Tổng chi phí ÷ Tổng OPP' },
          { label: 'Cost per Customer', value: formatVND(unifiedFunnel.customers > 0 ? totalSpend / unifiedFunnel.customers : 0), color: 'text-pink-400', tooltip: 'Chi phí trung bình để có 1 khách hàng mua gói.\n\nCông thức: Tổng chi phí ÷ Tổng customers' },
        ];

        return (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3 mb-6">
            {cards.map(card => (
              <div key={card.label} className={`rounded-lg p-3 ${isDark ? 'bg-white/5' : 'bg-gray-50 border border-gray-100'}`}>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  {card.label}
                  <InfoTooltip isDark={isDark} text={card.tooltip} />
                </p>
                <p className={`text-lg font-bold font-mono mt-1 ${isDark ? card.color : 'text-gray-900'}`}>{card.value}</p>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Conversion rate cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {rates.map((r) => (
          <div key={r.label} className={`rounded-lg p-3 ${isDark ? 'bg-white/5' : 'bg-gray-50 border border-gray-100'}`}>
            <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              {r.label}
              <InfoTooltip isDark={isDark} text={r.tooltip} />
            </p>
            <p className={`text-lg font-bold font-mono mt-1 ${isDark ? 'text-cyan-400' : 'text-gray-900'}`}>
              {Number(r.value).toFixed(2)}%
            </p>
          </div>
        ))}
      </div>

    </div>
  );
}
